import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import * as utils from './Utils';


export function formatCartItem(itemRow) {
  let productTitle = itemRow.name;
  let variantTitle = '';

  if (itemRow.options) {
    variantTitle = itemRow.options.map((option) => option.value).join("/") || '';
  }

  let item = {};
  item['id'] = `${itemRow.id}`;
  item['title'] = variantTitle ? `${productTitle} (${variantTitle})` : productTitle;
  item['quantity'] = itemRow.quantity;
  item['price'] = itemRow.salePrice;
  item['product_id'] = `${itemRow.productId}`;
  item['product_title'] = productTitle;
  item['variant_id'] = `${itemRow.variantId}`;
  item['variant_title'] = variantTitle;
  item['image_url'] = itemRow.imageUrl || '';
  return item;
}

export function formatCartResponse(response) {
  if (!response.id) return {};

  let data = {};
  data['currency'] = response.currency.code;
  data['total_price'] = response.cartAmount;
  data['total_discount'] = response.discountAmount;

  let items = [];
  if (response.lineItems) {
    response.lineItems.physicalItems.forEach((itemRow) => {
      let item = formatCartItem(itemRow);
      items.push(item);
    })

    response.lineItems.digitalItems.forEach((itemRow) => {
      let item = formatCartItem(itemRow);
      items.push(item);
    })
  }

  data['items'] = items;
  return data;
}


function getAddedCartItems(response) {
  const existingData = get(window.OnVoardEventsData, 'bigcommerce_cart', {});

  const existingMapper = {};
  if (existingData.lineItems) {
    existingData.lineItems.physicalItems.forEach((itemRow) => {
      existingMapper[`${itemRow.id}`] = parseInt(itemRow.quantity);
    })

    existingData.lineItems.digitalItems.forEach((itemRow) => {
      existingMapper[`${itemRow.id}`] = parseInt(itemRow.quantity);
    })
  }

  const currentMapper = {};
  if (response.lineItems) {
    response.lineItems.physicalItems.forEach((itemRow) => {
      currentMapper[`${itemRow.id}`] = parseInt(itemRow.quantity);
    })

    response.lineItems.digitalItems.forEach((itemRow) => {
      currentMapper[`${itemRow.id}`] = parseInt(itemRow.quantity);
    })
  }

  let addedIds = utils.getAddedCartItemIds(existingMapper, currentMapper);
  let addedItems = [];
  if (response.lineItems) {
    response.lineItems.physicalItems.forEach((itemRow) => {
      let itemId = `${itemRow.id}`;
      if (!addedIds.includes(itemId)) return;
      addedItems.push(itemRow)
    })

    response.lineItems.digitalItems.forEach((itemRow) => {
      let itemId = `${itemRow.id}`;
      if (!addedIds.includes(itemId)) return;
      addedItems.push(itemRow)
    })
  }

  return addedItems;
}

export function handleCartUpdate(response) {
  let noChange = isEqual(window.OnVoardEventsData['bigcommerce_cart'], response);
  let isNew = !window.OnVoardEventsData['bigcommerce_cart'] ? true : false;

  // no cart change
  if (noChange) return;

  // if first time loading, don't dispatch added cart items
  let addedCartItems = [];
  if (!isNew) addedCartItems = getAddedCartItems(response);

  window.OnVoardEventsData['bigcommerce_cart'] = response;
  utils.dispatchCartUpdateEvent(response);
  addedCartItems.forEach((item) => {
    utils.dispatchAddCartItemEvent(item);
  })
}

export function setupCartUpdateEvent() {
  if (window.OnVoardEventsData.setupCartUpdateEvent) return;
  window.OnVoardEventsData.setupCartUpdateEvent = true;

  let cartUpdatePaths = [
    "/api/storefront/carts",
  ];

  utils.interceptRequests(cartUpdatePaths, (response, url) => {
    if (url.includes('items')) return;
    response = utils.parseResponse(response);
    if (!response) return;
    if (response.status === 'error') return;

    if (!utils.isArray(response)) {
      handleCartUpdate(response);
      return;
    }

    let cart = {};
    if (response && response.length > 0) cart = response[0];
    handleCartUpdate(cart);
  });

  const triggerPaths = [
    "/remote/v1/cart/add",
    "/remote/v1/cart/update",
  ];

  let triggerCartUpdate = () => {
    utils.getRq({
      "url": "/api/storefront/carts?include=lineItems.physicalItems.options,lineItems.digitalItems.options",
      "headers": {
        "x-requested-with": "XMLHttpRequest",
      },
      "successCallback": (response) => {
        response = utils.parseResponse(response);
        if (!response) return;
        if (response.status === 'error') return;

        let cart = {};
        if (response && response.length > 0) cart = response[0];
        handleCartUpdate(cart);
      },
      "errorCallback": () => {}
    });
  };

  utils.interceptRequests(triggerPaths, (response, url) => {
    triggerCartUpdate();
  });
}

export function loadCart() {
  let successCallback = (response) => {
    response = utils.parseResponse(response);
    if (!response) return;
    if (response.status === 'error') return;

    let cart = {};
    if (response && response.length > 0) cart = response[0];
    handleCartUpdate(cart);
  };

  let errorCallback = () => {};

  utils.getRq({
    "url": "/api/storefront/carts?include=lineItems.physicalItems.options,lineItems.digitalItems.options",
    "headers": {
      "x-requested-with": "XMLHttpRequest",
    },
    "successCallback": successCallback,
    "errorCallback": errorCallback,
  });
}

export function addToCart(productId, kwargs={}) {
  // Adds a product to the BigCommerce storefront cart and, when a coupon code
  // is supplied (free-product rewards), applies it to the cart so the item is
  // discounted immediately — no manual code entry. For single-variant products
  // (the only kind allowed for reperks free-product rewards) the product id
  // alone is enough — BigCommerce resolves the default variant.
  const quantity = kwargs.quantity || 1;
  const couponCode = kwargs.couponCode || null;
  const successCallback = kwargs.successCallback || (() => {});
  const errorCallback = kwargs.errorCallback || (() => {});

  const lineItems = [{
    "productId": parseInt(productId),
    "quantity": quantity,
  }];

  const headers = {
    "Content-Type": "application/json",
    "x-requested-with": "XMLHttpRequest",
  };

  // The cart id doubles as the checkout id in BigCommerce. Apply the coupon to
  // it, then finish. A failed (or absent) coupon apply still counts as success
  // — the item is in the cart and the code stays visible as a fallback.
  const applyCouponThenFinish = (response) => {
    const cart = utils.parseResponse(response);
    const cartId = cart && cart.id ? cart.id : null;

    if (!couponCode || !cartId) {
      successCallback(response);
      return;
    }

    utils.postRq({
      "url": `/api/storefront/checkouts/${cartId}/coupons`,
      "data": JSON.stringify({ "couponCode": couponCode }),
      "headers": headers,
      // Coupon applied — send them to the cart so they see the discounted
      // (free) item immediately. On failure, fall back to the in-widget
      // success state (item added, code stays visible for manual entry).
      "successCallback": () => { window.location.href = "/cart.php"; },
      "errorCallback": () => successCallback(response),
    });
  };

  const createCart = () => {
    utils.postRq({
      "url": "/api/storefront/carts",
      "data": JSON.stringify({ "lineItems": lineItems }),
      "headers": headers,
      "successCallback": applyCouponThenFinish,
      "errorCallback": errorCallback,
    });
  };

  // Add to the existing cart if there is one, otherwise create a new cart.
  utils.getRq({
    "url": "/api/storefront/carts",
    "headers": { "x-requested-with": "XMLHttpRequest" },
    "successCallback": (response) => {
      response = utils.parseResponse(response);
      let cart = (response && response.length > 0) ? response[0] : null;

      if (cart && cart.id) {
        utils.postRq({
          "url": `/api/storefront/carts/${cart.id}/items`,
          "data": JSON.stringify({ "lineItems": lineItems }),
          "headers": headers,
          "successCallback": applyCouponThenFinish,
          "errorCallback": errorCallback,
        });
        return;
      }

      createCart();
    },
    "errorCallback": () => { createCart(); },
  });
}
